import React, { useEffect } from 'react';
import { Col, Input, Row, Switch, Tag } from 'antd';
import Form, { FormInstance } from 'antd/lib/form';

import { ClientFeatureConfig } from '../../../types/Feature';
import { QuoteDenyingConfigAttributes } from '../../../../../server/src/models';

enum WoopSlackAlertType {
    WOOP_QUOTE_DENIED = 'woop_quote_denied',
    WOOP_SAME_DAY_DELIVERY = 'woop_same_day_delivery',
    WOOP_UNKNOWN_ERROR = 'woop_unknown_error',
    WOOP_HIGH_DISTANCE_FEE = 'woop_high_distance_fee',
}

const cardHeaderTitleStyle = { fontSize: '18px' };

interface QuoteConfigFormSectionProps {
    isDetailMode: boolean;
    formInstance: FormInstance;
    config?: ClientFeatureConfig<QuoteDenyingConfigAttributes>;
}

const slackAlertLabels = {
    [WoopSlackAlertType.WOOP_QUOTE_DENIED]: 'Denied quotes',
    [WoopSlackAlertType.WOOP_SAME_DAY_DELIVERY]: 'Same day deliveries',
    [WoopSlackAlertType.WOOP_UNKNOWN_ERROR]: 'Other errors',
    [WoopSlackAlertType.WOOP_HIGH_DISTANCE_FEE]: 'High distance fee',
};

const QuoteConfigFormSection: React.FC<QuoteConfigFormSectionProps> = (props: QuoteConfigFormSectionProps) => {
    const { config, formInstance, isDetailMode } = props;

    useEffect(() => {
        if (config) {
            formInstance.setFields([
                {
                    name: 'bypassStuartAPIValidation',
                    value: config.config.bypassStuartAPIValidation,
                },
                {
                    name: 'sendSlackAlert',
                    value: config.config.sendSlackAlert,
                },
            ]);
        }
    }, [config]);

    return (
        <div className="card border-0" id="package-max-dimensions-section">
            <div className="card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                <p className="m-0 p-0 h5" style={cardHeaderTitleStyle}>
                    Configuration
                </p>
            </div>
            <div className="card-body">
                <Row gutter={24}>
                    <Col span={2} />
                    <Col span={11}>
                        <Form.Item
                            name="bypassStuartAPIValidation"
                            valuePropName="checked"
                            className="m-0"
                            tooltip={
                                <>
                                    Be careful, validation should only be disabled if your client is forwarded to the
                                    Routing Tool. Otherwise, your invalid request would be sent to Stuart API and
                                    denied.
                                </>
                            }
                            label="Bypass Stuart Validation"
                            labelCol={{ span: 16 }}
                        >
                            {isDetailMode ? (
                                <span className="ant-form-text text-secondary">
                                    <Tag color={config?.config.bypassStuartAPIValidation ? 'blue' : 'default'}>
                                        {config?.config.bypassStuartAPIValidation
                                            ? 'Validation bypassed'
                                            : 'Validation activated'}
                                    </Tag>
                                </span>
                            ) : (
                                <Switch
                                    checkedChildren="Validation bypassed"
                                    unCheckedChildren="Validation activated"
                                />
                            )}
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <div className="">
                        <p className="m-0 p-0 h6" style={{ fontSize: '14px', fontStyle: 'italic' }}>
                            Slack alerts
                        </p>
                        <p style={{ fontSize: '12px', opacity: 0.8 }}>
                            When an error occurs, an alert will be sent to the specified slack channel (or default slack
                            channel).
                        </p>
                    </div>
                </Row>
                {Object.values(WoopSlackAlertType).map((k) => (
                    <Row key={k} gutter={24}>
                        <Col span={2} />
                        <Col span={8} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6 }}>
                            <Form.Item
                                label={slackAlertLabels[k]}
                                name={['sendSlackAlert', k, 'activated']}
                                id={`sendSlackAlert[${k}].activated`}
                                valuePropName="checked"
                                labelCol={{ span: 16 }}
                            >
                                <Switch
                                    checkedChildren={<span>Enabled</span>}
                                    unCheckedChildren={<span>Disabled</span>}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6, offset: 1 }}>
                            <Form.Item
                                name={['sendSlackAlert', k, 'channel']}
                                label={<span className="mr-2">Slack channel</span>}
                                labelCol={{ span: 12 }}
                            >
                                {isDetailMode ? (
                                    <span className="ant-form-text text-secondary">
                                        {config?.config.sendSlackAlert?.[k]?.channel || '-'}
                                    </span>
                                ) : (
                                    <Input
                                        id={`sendSlackAlert[${k}].channel`}
                                        className="col-12"
                                        title="Woop Alert slack channel"
                                        autoComplete="off"
                                    />
                                )}
                            </Form.Item>
                        </Col>
                    </Row>
                ))}
            </div>
        </div>
    );
};

export default QuoteConfigFormSection;
